<template>
  <div class="role-settings-form">
    <FormTitle title="Role settings" />
    <div class="role-tab-container" v-if="isFormReady">
      <a-tabs type="card" v-model="activeRoleTab">
        <a-tab-pane
          v-for="(role, roleIndex) in availableRoles"
          :key="role.system_name"
          force-render
        >
          <template #tab>
            <DefaultLabelTab
              :name="role.name"
              :error_validate="availableRoles[roleIndex].error_validate"
            />
          </template>

          <a-form-model :model="form" :ref="`form-${role.system_name}`">
            <FormSubTitle title="General settings" />
            <div style="max-width: 500px;" class="d-flex flex-column">
              <a-form-model-item
                :prop="`${role.system_name}_settings.faq_set`"
                :rules="[{ required: true, message: 'FAQset is required', trigger: ['blur', 'change'] }]"
                :colon="false"
                class="label-with-tooltip"
              >
                <template #label>
                  <div>
                    <span class="mr-2 mb-0 font-weight-bold" style="line-height: initial;">FAQset</span>
                    <a-tooltip
                      placement="top"
                      arrow-point-at-center
                      :overlayStyle="{ 'white-space': 'normal' }"
                      :get-popup-container="getPopupContainer"
                    >
                      <template slot="title">
                        <span class="font-weight-light">These FAQs will show on the referral screen when the info icon is tapped</span>
                      </template>
                      <a-icon class="d-flex" type="info-circle" />
                    </a-tooltip>
                  </div>
                </template>
                <UniversalSelect
                  v-model="form[`${role.system_name}_settings`].faq_set"
                  style="width: 100%;"
                  :options="faqsetsOptions[`${role.system_name}`]"
                  :showSearch="true"
                  :readonly="isOnlyView ? !!form[`${role.system_name}_settings`].faq_set : false"
                  placeholder="Select FAQset"
                  :loading="getFaqsetsOptionsLoading"
                  :disabled="loading || isOnlyView || getFaqsetsOptionsLoading"
                  :searchCallback="faqsetsSearchCallback"
                  @searchCallback="getFaqsetsOptions(role, $event)"
                />
              </a-form-model-item>
              <a-form-model-item
                :prop="`${role.system_name}_settings.rule_set`"
                :rules="[{ required: true, message: 'Ruleset is required', trigger: ['blur', 'change'] }]"
                :colon="false"
                class="label-with-tooltip"
              >
                <template #label>
                  <div>
                    <span class="mr-2 mb-0 font-weight-bold" style="line-height: initial;">Ruleset</span>
                    <a-tooltip
                      placement="top"
                      arrow-point-at-center
                      :overlayStyle="{ 'white-space': 'normal' }"
                      :get-popup-container="getPopupContainer"
                    >
                      <template slot="title">
                        <span class="font-weight-light">Select the rules users must accept to access the program</span>
                      </template>
                      <a-icon class="d-flex" type="info-circle" />
                    </a-tooltip>
                  </div>
                </template>
                <UniversalSelect
                  v-model="form[`${role.system_name}_settings`].rule_set"
                  style="width: 100%;"
                  :options="rulesetsOptions[`${role.system_name}`]"
                  :showSearch="true"
                  :readonly="isOnlyView ? !!form[`${role.system_name}_settings`].rule_set : false"
                  placeholder="Select Ruleset"
                  :loading="getRulesetsOptionsLoading"
                  :disabled="loading || isOnlyView || getRulesetsOptionsLoading"
                  :searchCallback="rulesetsSearchCallback"
                  @searchCallback="getRulesetsOptions(role, $event)"
                />
              </a-form-model-item>
              <a-form-model-item
                v-if="role.system_name === 'affiliate'"
                :prop="`${role.system_name}_settings.avatars_category`"
                :rules="[{ required: true, message: 'Ruleset is required', trigger: ['blur', 'change'] }]"
                :colon="false"
                class="label-with-tooltip"
              >
                <template #label>
                  <div>
                    <span class="mr-2 mb-0 font-weight-bold" style="line-height: initial;">Avatar category</span>
                    <a-tooltip
                      placement="top"
                      arrow-point-at-center
                      :overlayStyle="{ 'white-space': 'normal' }"
                      :get-popup-container="getPopupContainer"
                    >
                      <template slot="title">
                        <span class="font-weight-light">Select a category to manage its avatar pool for this referral program</span>
                      </template>
                      <a-icon class="d-flex" type="info-circle" />
                    </a-tooltip>
                  </div>
                </template>
                <UniversalSelect
                  v-model="form[`${role.system_name}_settings`].avatars_category"
                  style="width: 100%;"
                  :options="avatarsOptions[`${role.system_name}`]"
                  :showSearch="true"
                  :readonly="isOnlyView ? !!form[`${role.system_name}_settings`].avatars_category : false"
                  placeholder="Select Avatar category"
                  :loading="getAvatarsOptionsLoading"
                  :disabled="loading || isOnlyView || getAvatarsOptionsLoading"
                  :searchCallback="avatarsSearchCallback"
                  @searchCallback="getAvatarsOptions(role, $event)"
                />
              </a-form-model-item>
              <div class="d-flex" style="gap: 20px;">
                <a-form-model-item
                  :prop="`${role.system_name}_settings.bet_sum`"
                  label="Bets sum"
                  :rules="[{ required: true, message: 'Bet sum is required', trigger: ['change'] }]"
                  :colon="false"
                  style="width: 100%;"
                >
                  <a-input-number
                    v-model="form[`${role.system_name}_settings`].bet_sum"
                    placeholder="Bets sum"
                    size="large"
                    allow-clear
                    :min="0"
                    :class="{ 'transparent-input transparent-input__view-mode' : isOnlyView }"
                    :disabled="loading || isOnlyView"
                    @keydown.native="keyPressInputNumber"
                  />
                </a-form-model-item>
                <a-form-model-item
                  v-if="role.system_name === 'affiliate'"
                  :prop="`${role.system_name}_settings.invited_referrals`"
                  label="Invited referrals"
                  :rules="[{ required: true, message: 'Invited referrals is required', trigger: ['change'] }]"
                  :colon="false"
                  style="width: 100%;"
                >
                  <a-input-number
                    v-model="form[`${role.system_name}_settings`].invited_referrals"
                    placeholder="Invited referrals"
                    size="large"
                    allow-clear
                    :class="{ 'transparent-input transparent-input__view-mode' : isOnlyView }"
                    :disabled="loading || isOnlyView"
                    @keydown.native="keyPressInputNumber($event, false)"
                  />
                </a-form-model-item>
              </div>
            </div>

            <div class="role-settings-form__container">
              <div class="role-settings-form__conditions mb-3">
                <FormSubTitle title="Conditions" />
                <Condition
                  v-for="(condition, index) in form[`${role.system_name}_settings`].conditions"
                  :key="index"
                  :ref="`${role.system_name}-conditions-${index}`"
                  :isOnlyView="isOnlyView"
                  :editPermission="editPermission"
                  :languagesUsed="languagesUsed"
                  :condition="condition"
                  @edit="toggleShowConditionModal(true, role.system_name, index, condition)"
                  @show="toggleShowConditionModal(true, role.system_name, index, condition)"
                />
              </div>
              <div class="role-settings-form__cashback">
                <FormSubTitle title="Cashback settings" />
                <div class="d-flex" style="gap: 20px; max-width: 500px;">
                  <a-form-model-item
                    :prop="`${role.system_name}_settings.cashback_percent`"
                    label="Cashback %"
                    :rules="[{ required: true, message: 'Cashback percent is required', trigger: ['change'] }]"
                    :colon="false"
                    style="width: 100%;"
                  >
                    <a-input-number
                      v-model="form[`${role.system_name}_settings`].cashback_percent"
                      placeholder="Cashback %"
                      size="large"
                      allow-clear
                      :min="0"
                      :max="100"
                      :class="{ 'transparent-input transparent-input__view-mode' : isOnlyView }"
                      :disabled="loading || isOnlyView"
                      @keydown.native="keyPressInputNumber"
                    />
                  </a-form-model-item>
                  <a-form-model-item
                    :prop="`${role.system_name}_settings.cashback_limit`"
                    label="Cashback limit"
                    :rules="[{ required: true, message: 'Cashback limit is required', trigger: ['change'] }]"
                    :colon="false"
                    style="width: 100%;"
                  >
                    <a-input-number
                      v-model="form[`${role.system_name}_settings`].cashback_limit"
                      placeholder="Cashback limit"
                      size="large"
                      allow-clear
                      :min="0"
                      :class="{ 'transparent-input transparent-input__view-mode' : isOnlyView }"
                      :disabled="loading || isOnlyView"
                      @keydown.native="keyPressInputNumber"
                    />
                  </a-form-model-item>
                </div>
              </div>
            </div>

            <FormSubTitle
              title="Additional conditions description"
              tooltipText="This description will show on the referral screen when the condition icon is tapped"
            />

            <div class="additional-tabs">
              <a-tabs type="card" v-model="activeAdditionalConditionTabIndex">
                <a-tab-pane
                  v-for="(additional, addIdx) in form[`${role.system_name}_settings`].additional_conditions"
                  :key="addIdx"
                  force-render
                >
                  <template #tab>
                    <DefaultLabelTab
                      :name="additional.tab_name"
                      :is_default="additional.is_default"
                      :error_validate="additional.error_validate"
                    />
                  </template>
                  <div class="d-flex justify-content-between align-items-center">
                    <a-button
                      v-if="!isOnlyView"
                      type="primary"
                      size="large"
                      :disabled="!editPermission"
                      @click="toggleShowConditionDescriptionModal(true)"
                    >
                      <a-icon type="plus" /> Add
                    </a-button>
                  </div>
                  <div class="d-flex flex-wrap" style="max-width: 665px; gap: 15px;">
                    <div v-if="!additional.items.length" class="text-secondary mt-4">
                      <span>No items have been added</span>
                    </div>
                    <div v-else class="mt-4 w-full">
                      <draggable
                        v-model="additional.items"
                        v-bind="dragOptions"
                        handle=".handle"
                        @start="drag = true"
                        @end="drag = false"
                      >
                        <transition-group tag="div" class="additional-conditions">
                          <div
                            v-for="item in additional.items"
                            :key="item.uniqueId"
                            class="additional-condition"
                          >
                            <div v-if="!isOnlyView" class="d-flex align-items-center">
                              <a-icon
                                type="menu"
                                class="drag-icon handle role-settings-form__example__icon role-settings-form__example__icon__hide"
                                :class="{'role-settings-form__example__icon__show mr-2' : additional.items.length > 1}"
                              />
                            </div>
                            <div class="additional-condition__content">
                              <span class="additional-condition__title truncate">{{ item.name }}</span>
                              <div
                                class="d-flex"
                                :class="{'rule__pl-28' : !isOnlyView}"
                                style="gap: 20px"
                              >
                                <div class="d-flex justify-content-end">
                                  <a-button
                                    v-if="!isOnlyView"
                                    type="link"
                                    :disabled="!editPermission"
                                    @click="toggleShowConditionDescriptionModal(true, item)"
                                  >
                                    <a-icon type="edit" /> Edit
                                  </a-button>
                                  <a-button
                                    v-else
                                    type="link"
                                    @click="toggleShowConditionDescriptionModal(true, item)"
                                  >
                                    <a-icon type="eye" />
                                  </a-button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </transition-group>
                      </draggable>
                    </div>
                  </div>
                </a-tab-pane>
              </a-tabs>
            </div>
          </a-form-model>
        </a-tab-pane>
      </a-tabs>
    </div>
    <ConditionModal
      v-if="showConditionModal"
      :show="showConditionModal"
      :languagesUsed="languagesUsed"
      :selectedCondition="targetCondition"
      :isOnlyView="isOnlyView"
      @update="updateCondition"
      @close="toggleShowConditionModal"
    />
    <ConditionDescriptionModal
      v-if="showConditionDescriptionModal"
      :show="showConditionDescriptionModal"
      :selectedCondition="targetConditionDesc"
      :isOnlyView="isOnlyView"
      @create="createConditionDesc"
      @update="updateConditionDesc"
      @delete="deleteConditionDesc"
      @close="toggleShowConditionDescriptionModal"
    />
  </div>
</template>

<script>
import FormTitle from '@/components/custom/form/FormTitle.vue'
import FormSubTitle from '@/components/custom/form/FormSubTitle.vue'
import DefaultLabelTab from '@/views/apps/referral-program/components/DefaultLabelTab.vue'
import UniversalSelect from '@/components/custom/filters/universalSelect.vue'
import Condition from '../components/Condition.vue'
import ConditionModal from '../components/ConditionModal.vue'
import ConditionDescriptionModal from '../components/ConditionDescriptionModal.vue'
import draggable from 'vuedraggable'
import cloneDeep from 'lodash/cloneDeep'
import { getReferralRoles } from '@/views/apps/referral-program/composables/useGetReferralRoles.js'
import { getOptions } from '@/views/apps/referral-program/composables/useGetOptions.js'
import { handleNumberKeyPress } from '@/views/apps/referral-program/composables/useHandleNumberKeyPress.js'
import { getRandomId } from '@/composables/useGetRandomId.js'

export default {
  props: {
    languagesUsed: {
      type: Array,
      default: () => [],
    },
    isOnlyView: {
      type: Boolean,
      default: false,
    },
    editPermission: {
      type: Boolean,
      default: false,
    },
    errorFormValidate: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    FormTitle,
    FormSubTitle,
    DefaultLabelTab,
    UniversalSelect,
    draggable,
    Condition,
    ConditionModal,
    ConditionDescriptionModal,
  },
  data: () => ({
    programId: null,
    activeRoleTab: undefined,
    activeAdditionalConditionTabIndex: 0,
    form: {},
    isFormReady: false,

    availableRoles: [],
    getAvailableRolesLoading: false,

    drag: false,

    defaultMaxOptions: 50,
    defaultAllConditions: [
      { type: 'join_program', translations: [] },
      { type: 'invite_referrals', translations: [] },
      { type: 'referral_bet_sum', translations: [] },
      { type: 'affiliate_bet_sum', translations: [] },
    ],

    faqsetsOptions: {},
    faqsetsSearchCallback: false,
    getFaqsetsOptionsLoading: false,

    rulesetsOptions: {},
    rulesetsSearchCallback: false,
    getRulesetsOptionsLoading: false,

    avatarsOptions: {},
    avatarsSearchCallback: false,
    getAvatarsOptionsLoading: false,

    targetCondition: null,
    showConditionModal: false,
    targetConditionDesc: null,
    showConditionDescriptionModal: false,
  }),
  watch: {
    form: {
      async handler() {
        if (!this.errorFormValidate) {
          return
        }
        const isValidRoleSettingsForm = await this.validateFields()
        if (!isValidRoleSettingsForm) {
          return
        }
        this.$emit('clearError')
      },
      deep: true,
    },
    languagesUsed: {
      handler(val, oldVal) {
        if (!oldVal?.length) {
          return
        }
        this.syncAdditionalConditions()
      },
      deep: true,
    },
  },
  computed: {
    isEditMode() {
      return !!this.programId
    },
    dragOptions() {
      return {
        animation: 300,
        disabled: false,
        ghostClass: 'ghost',
      }
    },
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    keyPressInputNumber(event, allowDot = true) {
      handleNumberKeyPress(event, allowDot)
    },
    syncAdditionalConditions() {
      this.availableRoles.forEach(role => {
        const existingConditions = this.form[`${role.system_name}_settings`].additional_conditions

        // Обновляем conditions
        this.form[`${role.system_name}_settings`].conditions.forEach(condition => {
          const existingTranslations = condition.translations || []

          // Обновляем список переводов с учетом всех языков
          const updatedTranslations = this.languagesUsed.map(lang => {
            const existing = existingTranslations.find(t => t.language_id === lang.value)
            return existing || {
              language_id: lang.value,
              name: '',
            }
          })

          // Присваиваем отсортированные переводы обратно
          condition.translations = updatedTranslations
        })

        const updatedConditions = this.languagesUsed.map(lang => {
          const existing = existingConditions.find(condition => condition.language_id === lang.value)
          if (existing) {
            return {
              ...existing,
              is_default: lang.is_default,
              error_validate: false,
            }
          }

          // если такого языка нет — добавляем его
          return {
            language_id: lang.value,
            tab_name: lang.name,
            is_default: lang.is_default,
            items: [],
            error_validate: false,
          }
        })

        // Сортируем так, чтобы язык с is_default: true был первым
        updatedConditions.sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))

        this.activeAdditionalConditionTabIndex = 0
        this.form[`${role.system_name}_settings`].additional_conditions = updatedConditions
      })
    },
    toggleShowConditionModal(status = false, system_name = null, conditionIndex = null, condition = null) {
      this.targetCondition = { ...condition, system_name: system_name, index: conditionIndex }
      this.showConditionModal = status
    },
    toggleShowConditionDescriptionModal(status = false, condition = null) {
      this.targetConditionDesc = condition
      this.showConditionDescriptionModal = status
    },
    async setDefaultValues() {
      if (!this.availableRoles?.length) {
        await this.waitForRolesOptions()
      }
      this.availableRoles.forEach(role => {
        // TODO Добавить при редактировании
        // if (id) {
        //   this.addFieldForForm(id, 'id', id)
        // }
        // this.addFieldForForm(`${role.system_name}_settings`, 'role', role)
        this.addFieldForForm(`${role.system_name}_settings`, 'faq_set', undefined)
        this.addFieldForForm(`${role.system_name}_settings`, 'rule_set', undefined)
        this.addFieldForForm(`${role.system_name}_settings`, 'bet_sum', undefined)
        this.addFieldForForm(`${role.system_name}_settings`, 'cashback_percent', undefined)
        this.addFieldForForm(`${role.system_name}_settings`, 'cashback_limit', undefined)

        this.addFieldForForm(`${role.system_name}_settings`, 'additional_conditions',
          this.languagesUsed.map(lang => {
            return {
              language_id: lang.value,
              tab_name: lang.name,
              is_default: lang.is_default,
              items: [],
              error_validate: false,
            }
          }),
        )

        if (role.system_name === 'affiliate') {
          this.addFieldForForm(`${role.system_name}_settings`, 'avatars_category', undefined)
          this.addFieldForForm(`${role.system_name}_settings`, 'invited_referrals', undefined)

          const clonedConditions = cloneDeep(this.defaultAllConditions)
          this.addFieldForForm(`${role.system_name}_settings`, 'conditions', clonedConditions)
        }

        if (role.system_name === 'referral') {
          const filteredConditions = this.defaultAllConditions.filter(condition => condition.type !== 'invite_referrals')
          const clonedConditions = cloneDeep(filteredConditions)
          this.addFieldForForm(`${role.system_name}_settings`, 'conditions', clonedConditions)
        }

        this.getFaqsetsOptions(role)
        this.getRulesetsOptions(role)
        this.getAvatarsOptions(role)
      })
      this.isFormReady = true
    },
    async setFormValues(data) {
      const { affiliate_settings, referral_settings } = data

      if (!affiliate_settings || !referral_settings) {
        this.setDefaultValues()
        return
      }

      if (!this.availableRoles?.length) {
        await this.waitForRolesOptions()
      }

      this.availableRoles.forEach(role => {
        const settings = data[`${role.system_name}_settings`]

        this.addFieldForForm(`${role.system_name}_settings`, 'faq_set', settings.faq_set?.id)
        this.addFieldForForm(`${role.system_name}_settings`, 'rule_set', settings.rule_set?.id)
        this.addFieldForForm(`${role.system_name}_settings`, 'bet_sum', settings.bet_sum)
        this.addFieldForForm(`${role.system_name}_settings`, 'cashback_percent', settings.cashback_percent)
        this.addFieldForForm(`${role.system_name}_settings`, 'cashback_limit', settings.cashback_limit)
        this.addFieldForForm(`${role.system_name}_settings`, 'conditions', settings.conditions)

        const incomingAdditionalConditions = data[`${role.system_name}_settings`].additional_conditions || []

        this.addFieldForForm(`${role.system_name}_settings`, 'additional_conditions',
          this.languagesUsed.map(lang => {
            const itemsForLang = incomingAdditionalConditions.filter(item => item.language_id === lang.value).map(item => ({
              ...item,
              uniqueId: item.id || getRandomId(),
            }))

            return {
              language_id: lang.value,
              tab_name: lang.name,
              is_default: lang.is_default,
              items: itemsForLang,
              error_validate: false,
            }
          }),
        )

        if (role.system_name === 'affiliate') {
          this.addFieldForForm(`${role.system_name}_settings`, 'avatars_category', settings.avatar_category?.id)
          this.addFieldForForm(`${role.system_name}_settings`, 'invited_referrals', settings.invited_referrals)
        }
        this.getFaqsetsOptions(role)
        this.getRulesetsOptions(role)
        this.getAvatarsOptions(role)
      })
      this.isFormReady = true
    },
    addFieldForForm(target, field, value) {
      if (!this.form[target]) {
        this.$set(this.form, target, {})
      }
      this.$set(
        this.form[`${target}`],
        field,
        value,
      )
    },
    updateCondition({ translations, system_name, index }) {
      this.form[`${system_name}_settings`].conditions[index].translations = translations.map(translation => {
        return { language_id: translation.language_id, name: translation.name }
      })
    },
    createConditionDesc(conditionDescName) {
      let currentAdditionalIds = []

      if (this.form[`${this.activeRoleTab}_settings`].additional_conditions[this.activeAdditionalConditionTabIndex].items.length) {
        currentAdditionalIds = this.form[`${this.activeRoleTab}_settings`].additional_conditions[this.activeAdditionalConditionTabIndex].items.map(item => item.uniqueId)
      }

      this.form[`${this.activeRoleTab}_settings`].additional_conditions[this.activeAdditionalConditionTabIndex].items.push({
        uniqueId: getRandomId(currentAdditionalIds),
        language_id: this.form[`${this.activeRoleTab}_settings`].additional_conditions[this.activeAdditionalConditionTabIndex].language_id,
        name: conditionDescName,
      })
      this.clearAdditionalConditionError()
    },
    updateConditionDesc(conditionDesc) {
      const oldConditionDescIdx = this.form[`${this.activeRoleTab}_settings`].additional_conditions[this.activeAdditionalConditionTabIndex].items.findIndex(item => item.uniqueId === conditionDesc.uniqueId)
      if (oldConditionDescIdx === -1) {
        return
      }
      this.form[`${this.activeRoleTab}_settings`].additional_conditions[this.activeAdditionalConditionTabIndex].items.splice(oldConditionDescIdx, 1, conditionDesc)
    },
    deleteConditionDesc(conditionDesc) {
      const oldConditionDescIdx = this.form[`${this.activeRoleTab}_settings`].additional_conditions[this.activeAdditionalConditionTabIndex].items.findIndex(item => item.uniqueId === conditionDesc.uniqueId)
      if (oldConditionDescIdx === -1) {
        return
      }
      this.form[`${this.activeRoleTab}_settings`].additional_conditions[this.activeAdditionalConditionTabIndex].items.splice(oldConditionDescIdx, 1)
    },
    clearAdditionalConditionError() {
      this.form[`${this.activeRoleTab}_settings`].additional_conditions[this.activeAdditionalConditionTabIndex].error_validate = false
    },
    async getAvailableRoles() {
      try {
        this.getAvailableRolesLoading = true
        const roles = await getReferralRoles() || []
        this.availableRoles = roles.map(role => {
          return {
            id: role.id,
            name: role.name,
            system_name: role.system_name,
            error_validate: false,
          }
        })
        this.activeRoleTab = this.availableRoles[0].system_name
      } finally {
        this.getAvailableRolesLoading = false
      }
    },
    waitForRolesOptions() {
      return new Promise((resolve) => {
        const check = () => {
          if (this.availableRoles.length) {
            resolve()
          } else {
            setTimeout(check, 100)
          }
        }
        check()
      })
    },
    async getFaqsetsOptions(role, name = '') {
      const roleId = role.id
      const systemRoleName = role.system_name
      const response = await getOptions(this, 'getFaqsetsOptionsLoading', 'faq-sets', this.defaultMaxOptions, roleId, name)
      if (!response) {
        return
      }
      const { data, meta } = response
      const callbackName = name ? null : 'faqsetsSearchCallback'
      this.setOptions(data, meta, 'faqsetsOptions', systemRoleName, callbackName)
    },
    async getRulesetsOptions(role, name = '') {
      const roleId = role.id
      const systemRoleName = role.system_name
      const response = await getOptions(this, 'getRulesetsOptionsLoading', 'rule-sets', this.defaultMaxOptions, roleId, name)
      if (!response) {
        return
      }
      const { data, meta } = response
      this.setOptions(data, meta, 'rulesetsOptions', systemRoleName, 'rulesetsSearchCallback')
    },
    async getAvatarsOptions(role, name = '') {
      const roleId = role.id
      const systemRoleName = role.system_name
      const response = await getOptions(this, 'getAvatarsOptionsLoading', 'avatar-packages', this.defaultMaxOptions, roleId, name)
      if (!response) {
        return
      }
      const { data, meta } = response
      this.setOptions(data, meta, 'avatarsOptions', systemRoleName, 'avatarsSearchCallback')
    },
    setOptions(data, meta, optionsName, roleName, callbackName = null) {
      if (!this[`${optionsName}`][`${roleName}`]) {
        this.$set(
          this[`${optionsName}`],
          roleName,
          [],
        )
      }
      this[`${optionsName}`][`${roleName}`] = data.map(option => {
        return {
          name: option.name,
          value: option.id,
        }
      })
      if (callbackName) {
        // Определяем делать ли поиск по опциям используя запрос к серверу
        this[`${callbackName}`] = meta.total > this.defaultMaxOptions
      }
    },
    getPayload() {
      const payload = {}
      this.availableRoles.forEach(role => {
        payload[`${role.system_name}_settings`] = {}
        payload[`${role.system_name}_settings`].faq_set_id = this.form[`${role.system_name}_settings`].faq_set
        payload[`${role.system_name}_settings`].rule_set_id = this.form[`${role.system_name}_settings`].rule_set
        payload[`${role.system_name}_settings`].bet_sum = this.form[`${role.system_name}_settings`].bet_sum
        payload[`${role.system_name}_settings`].cashback_percent = this.form[`${role.system_name}_settings`].cashback_percent
        payload[`${role.system_name}_settings`].cashback_limit = this.form[`${role.system_name}_settings`].cashback_limit
        payload[`${role.system_name}_settings`].conditions = this.form[`${role.system_name}_settings`].conditions

        const additionalConditions = this.form[`${role.system_name}_settings`].additional_conditions.flatMap(ac => {
          return ac.items.map(item => ({
            language_id: item.language_id,
            name: item.name,
          }))
        })
        payload[`${role.system_name}_settings`].additional_conditions = additionalConditions

        if (role.system_name === 'affiliate') {
          payload[`${role.system_name}_settings`].avatar_package_id = this.form[`${role.system_name}_settings`].avatars_category
          payload[`${role.system_name}_settings`].invited_referrals = this.form[`${role.system_name}_settings`].invited_referrals
        }
      })
      return payload
    },
    async validateFields() {
      return new Promise((resolve) => {
        let isValid = true

        this.availableRoles.forEach((role, index) => {
          if (!this.$refs[`form-${role.system_name}`]) {
            isValid = false
            return
          }
          this.form[`${role.system_name}_settings`].conditions.forEach((_, index) => {
            this.$refs[`${role.system_name}-conditions-${index}`][0].validateCodition()
          })

          this.$refs[`form-${role.system_name}`][0].validate((valid) => {
            if (!valid) {
              this.availableRoles[index].error_validate = true
              isValid = false
            } else {
              this.availableRoles[index].error_validate = false
            }
          })
        })
        resolve(isValid)
      })
    },
  },
  mounted() {
    this.programId = this.$route.params.id

    this.getAvailableRoles()
    if (this.isEditMode) {
      return
    }
    this.setDefaultValues()
  },
}
</script>

<style lang="scss">
.role-settings-form {
  height: 100%;
  &__container {
    display: flex;
    flex-wrap: wrap;
    gap: 200px;
    @media (max-width: 768px) {
      gap: 20px;
    }
  }
  &__conditions {
    max-width: 500px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  &__cashback {
    max-width: 500px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1 1 auto;
  }
  //
  &__example {
    transition: width 2s ease;
    &__icon {
      display: flex;
      justify-content: center;
      align-items: center;
      align-self: flex-start;
      height: 40px;
      width: 20px;
      min-width: 20px;
      transition: all .3s ease;
      &__hide {
        width: 0;
        min-width: 0;
      }
      &__show {
        width: 20px;
        min-width: 20px;
      }
    }
  }
  .additional-conditions {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .additional-condition {
    display: flex;
    &__content {
      max-width: 500px;
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 20px;
      padding: 20px;
      border: 1px solid #e4e9f0;
      border-radius: 5px;
      background-color: #fff;
    }
    &__title {
      display: block;
    }
    &__pl-28 {
      padding-left: 28px;
    }
  }
  //

  .role-tab-container {
    overflow: hidden;
  }
  .role-tab-container > .ant-tabs-card > .ant-tabs-content {
    margin-top: -16px;
  }

  .role-tab-container > .ant-tabs-card > .ant-tabs-content > .ant-tabs-tabpane {
    background: rgba(0, 123, 255, 0.05) !important;
    padding: 16px;
  }

  .role-tab-container > .ant-tabs-card > .ant-tabs-bar {
    border-color: rgba(0, 123, 255, 0.05) !important;
  }

  .role-tab-container > .ant-tabs-card > .ant-tabs-bar .ant-tabs-tab {
    border-color: transparent;
    background: transparent;
    margin-right: 0 !important;
  }

  .role-tab-container > .ant-tabs-card > .ant-tabs-bar .ant-tabs-tab-active {
    border-color: rgba(0, 123, 255, 0.05) !important;
    background: rgba(0, 123, 255, 0.05) !important;
  }

  .additional-tabs > .ant-tabs-card > .ant-tabs-bar .ant-tabs-tab {
    margin-right: 0 !important;
  }

  .additional-tabs > .ant-tabs-card > .ant-tabs-content > .ant-tabs-tabpane {
    background: #fff;
  }

  .additional-tabs > .ant-tabs-card > .ant-tabs-content {
    margin-top: -16px;
  }

  .additional-tabs > .ant-tabs-card > .ant-tabs-content > .ant-tabs-tabpane {
    border-left: 1px solid #e4e9f0;
    border-bottom: 1px solid #e4e9f0;
    border-right: 1px solid #e4e9f0;
    padding: 16px;
  }

  .ant-input-number {
    width: 100%;
  }

  .ant-input-number-handler-wrap {
    display: none !important;
  }
}
</style>
